import * as ToastPrimitives from "@radix-ui/react-toast";
import { Check, CircleAlert, Info, TriangleAlert } from "lucide-react";
import type { JSX, MouseEvent, ReactElement, ReactNode } from "react";
import { isValidElement, useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

// Toasts in the v3.2 shell (VITE_APP_REDESIGN_ENABLED): a small pill, ink
// for confirmations and the record red tone for errors, gone after ~2 s. A
// toast with an action (Undo) stays 10 s, and Radix keeps it while it is
// hovered or focused (staging check 04/11: Undo was gone before it could be
// pressed).
// Phones: centred at the top under the header, so it never covers the
// header actions; with a bottom sheet or drawer open, just above the sheet.
// Desktop: bottom centre, above the Ask bar.

export const V3_TOAST_DURATION_MS = 2000;
export const V3_TOAST_ACTION_DURATION_MS = 10000;
const PHONE_MAX = 1023;

// The top edge of an open bottom sheet (its distance from the bottom of the
// screen), or null.
const openSheetLift = (): number | null => {
  const dialogs = document.querySelectorAll<HTMLElement>(
    '[role="dialog"][data-state="open"]',
  );
  let lift: number | null = null;
  for (const dialog of dialogs) {
    const rect = dialog.getBoundingClientRect();
    const docked = Math.abs(rect.bottom - window.innerHeight) < 2;
    if (docked && rect.top > 0 && rect.width >= window.innerWidth * 0.9)
      lift = Math.max(lift ?? 0, window.innerHeight - rect.top);
  }
  return lift;
};

// Desktop: the top edge of the visit's Ask dock (it grows with attached
// files and status lines), so a toast sits above the files and the
// suggestions instead of on them; null without a dock.
const askDockLift = (): number | null => {
  const dock = document.querySelector<HTMLElement>("[data-toast-anchor]");
  if (!dock) return null;
  const rect = dock.getBoundingClientRect();
  if (rect.height === 0) return null;
  // The dock starts with a 32px fade; the toast may sit in it.
  return Math.max(0, window.innerHeight - rect.top - 32);
};

const useSheetLift = (active: boolean): number | null => {
  const [lift, setLift] = useState<number | null>(null);
  useEffect(() => {
    if (!active) return;
    const update = (): void => {
      setLift(
        window.innerWidth <= PHONE_MAX
          ? openSheetLift()
          : (() => {
              const dock = askDockLift();
              // Only when the dock reaches above the usual spot.
              return dock !== null && dock + 12 > 104 ? dock : null;
            })(),
      );
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-state"],
    });
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [active]);
  return lift;
};

const ICON = {
  success: Check,
  default: Check,
  info: Info,
  warning: TriangleAlert,
  error: CircleAlert,
  destructive: CircleAlert,
} as const;

type ActionProps = {
  altText?: string;
  children?: ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

// A toast's `action` (a classic <ToastAction>) redrawn as a v3 text button
// inside the pill: same label, alt text and click handler.
const V3ToastAction = ({
  action,
  error,
}: {
  action: unknown;
  error: boolean;
}): JSX.Element | null => {
  if (!isValidElement(action)) return null;
  const { altText, children, onClick } = (action as ReactElement<ActionProps>)
    .props;
  return (
    <ToastPrimitives.Action
      altText={altText ?? (typeof children === "string" ? children : "")}
      className={cn(
        "-my-1 -mr-2 ml-1 inline-flex min-h-8 shrink-0 items-center rounded-full px-3 text-[13.5px] font-semibold underline-offset-2 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-v3-brand-ring",
        error
          ? "text-v3-rec-text hover:bg-[color-mix(in_srgb,var(--v3-rec)_14%,transparent)]"
          : "text-v3-on-ink hover:bg-[color-mix(in_srgb,var(--v3-on-ink)_14%,transparent)]",
      )}
      data-testid="toast-v3-action"
      onClick={onClick}
    >
      {children}
    </ToastPrimitives.Action>
  );
};

export const ToasterV3 = (): JSX.Element => {
  const { toasts } = useToast();
  const lift = useSheetLift(toasts.length > 0);
  return (
    <ToastPrimitives.Provider
      duration={V3_TOAST_DURATION_MS}
      swipeDirection="up"
    >
      {toasts.map(
        ({ id, title, description, variant, duration, action, ...props }) => {
          const error = variant === "error" || variant === "destructive";
          const Icon = ICON[variant ?? "default"] ?? Check;
          // A toast with only a description (e.g. "Copied") shows it as the
          // label; a confirmation with a title keeps to the title.
          const label = title ?? description;
          const detail = title && error ? description : undefined;
          return (
            <ToastPrimitives.Root
              key={id}
              {...props}
              className={cn(
                "pointer-events-auto flex max-w-[min(calc(100vw-32px),480px)] items-center gap-2 py-2 pl-3 pr-4",
                detail ? "rounded-[20px]" : "rounded-full",
                "font-v3 text-[14px] font-semibold shadow-v3-pop data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
                error
                  ? "bg-[color-mix(in_srgb,var(--v3-rec)_12%,var(--v3-panel))] text-v3-rec-text ring-1 ring-[color-mix(in_srgb,var(--v3-rec)_30%,transparent)]"
                  : "bg-v3-ink text-v3-on-ink",
              )}
              data-testid="toast-v3"
              data-variant={error ? "error" : "ok"}
              // Errors stay a little longer to be read; one with an action
              // long enough to use it.
              duration={
                isValidElement(action)
                  ? Math.max(duration ?? 0, V3_TOAST_ACTION_DURATION_MS)
                  : error
                    ? Math.max(duration ?? 0, 4000)
                    : V3_TOAST_DURATION_MS
              }
            >
              <span
                aria-hidden
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full",
                  error ? "" : "bg-v3-ok text-white",
                )}
              >
                <Icon className={error ? "size-4" : "size-3"} strokeWidth={3} />
              </span>
              <div className="min-w-0">
                {label && (
                  <ToastPrimitives.Title
                    className={cn(detail ? "" : "line-clamp-2")}
                  >
                    {label}
                  </ToastPrimitives.Title>
                )}
                {detail && (
                  <ToastPrimitives.Description className="text-[12.5px] font-normal opacity-90">
                    {detail}
                  </ToastPrimitives.Description>
                )}
              </div>
              <V3ToastAction action={action} error={error} />
            </ToastPrimitives.Root>
          );
        },
      )}
      <ToastPrimitives.Viewport
        className={cn(
          "pointer-events-none fixed inset-x-0 z-[100] flex flex-col items-center gap-2 px-4 outline-none",
          lift !== null
            ? ""
            : "top-[calc(env(safe-area-inset-top,0px)+64px)] lg:top-auto lg:bottom-[104px]",
        )}
        data-testid="toast-v3-viewport"
        style={lift !== null ? { bottom: lift + 12 } : undefined}
      />
    </ToastPrimitives.Provider>
  );
};
